import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import type { ReactNode } from 'react'
import WidgetCard from 'features/executive/components/WidgetCard/WidgetCard'
import {
  formatCount,
  formatCurrency,
  formatPercent,
  formatVariance,
} from 'utils/executive.utils'

export interface LeaderboardRow {
  id: number
  name: string
  revenue: number
  count: number       // caseCount or unitsSold
  avgPerItem: number  // avgPerCase or avgPerUnit
  percentShare: number
  // Per-row variance vs the same-duration previous period. When provided,
  // the revenue cell renders a small ▲/▼ percentage under the amount.
  // Use null (or omit) to hide it — e.g. older backend that doesn't return
  // previousRevenue, or rows with no comparable previous data.
  variance?: number | null
}

export interface LeaderboardCardProps {
  title: string
  // Optional ⓘ tooltip rendered next to the title (formula/glossary).
  titleTooltip?: string
  // Optional caption rendered under the title before the table — e.g.
  // "Top 10 • รวม ฿X,XXX,XXX". Any ReactNode is allowed for inline emphasis.
  subtitle?: ReactNode
  // Optional notice block rendered between subtitle and table — e.g.
  // a concentration warning chip. Hidden when undefined.
  notice?: ReactNode
  // Custom message for EmptyState. Falls back to the default i18n message.
  emptyMessage?: string
  countColumnLabel: string   // "Cases" or "Units"
  avgColumnLabel: string     // "Avg / Case" or "Avg / Unit"
  countUnit: string          // "เคส" or "หน่วย"
  rows: LeaderboardRow[]
  totalRevenue: number
  loading: boolean
  error: Error | null
  onRetry: () => void
  onClick?: () => void
  // i18n column headers passed in so widget wrappers can translate.
  columnHeaders: {
    rank: string
    name: string
    revenue: string
    share: string
  }
}

export default function LeaderboardCard({
  title,
  titleTooltip,
  subtitle,
  notice,
  emptyMessage,
  countColumnLabel,
  avgColumnLabel,
  countUnit,
  rows,
  loading,
  error,
  onRetry,
  onClick,
  columnHeaders,
}: LeaderboardCardProps) {
  const isEmpty = !loading && !error && rows.length === 0

  return (
    <WidgetCard
      title={title}
      titleTooltip={titleTooltip}
      subtitle={subtitle}
      notice={notice}
      loading={loading}
      error={error}
      empty={isEmpty}
      emptyMessage={emptyMessage}
      onRetry={onRetry}
      onClick={onClick}
      skeletonHeight={380}
    >
      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 40 }}>{columnHeaders.rank}</TableCell>
              <TableCell>{columnHeaders.name}</TableCell>
              <TableCell align="right">{columnHeaders.revenue}</TableCell>
              <TableCell align="right">{countColumnLabel}</TableCell>
              <TableCell align="right">{avgColumnLabel}</TableCell>
              <TableCell align="right">{columnHeaders.share}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r, idx) => (
              <TableRow key={r.id}>
                <TableCell>{idx + 1}</TableCell>
                <TableCell>{r.name}</TableCell>
                <TableCell align="right">
                  {formatCurrency(r.revenue)}
                  {r.variance != null && (
                    <Typography
                      variant="caption"
                      sx={{
                        display: 'block',
                        color: r.variance >= 0 ? 'success.main' : 'error.main',
                      }}
                    >
                      {formatVariance(r.variance)}
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="right">{formatCount(r.count, countUnit)}</TableCell>
                <TableCell align="right">{formatCurrency(r.avgPerItem)}</TableCell>
                <TableCell align="right">{formatPercent(r.percentShare, 1)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </WidgetCard>
  )
}
